import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import { fetchRequest } from '@tk/utilities/tk.fetch';
import isValidJSON from '@tk/utilities/tk.valid.json';
import parseBooleanOrString from '@tk/utilities/tk.parse.boolean';
import toSnakeCase from '@tk/utilities/tk.snake.case';

type Items = Record<string, string | boolean>[];

interface Attribute {
    key: string;
    value: string | boolean | Items;
}

interface AsyncAttributeResponse {
    attributes: Record<string, unknown>;
    [key: string]: unknown;
}

export default class TKTracking extends TKCustomElementFactory {
    trackerElement?: HTMLElement;
    event?: string;
    location: string;
    asyncAttributesURL?: string;
    trackerType?: string;
    hasItems: boolean = false;
    itemsKey: string;

    attributeList: Attribute[] = [];

    isAsync: boolean;

    constructor() {
        super();

        this.trackerElement = this.closest('[data-tk-tracker-wrapper]') || undefined;
        this.event = this.getAttribute('data-tk-event') || undefined;
        this.isAsync = this.hasAttribute('data-tk-is-async');
        this.asyncAttributesURL = this.getAttribute('data-tk-async-attributes-url') || undefined;
        this.trackerType = this.getAttribute('data-tk-tracker-type') || undefined;
        this.hasItems = this.hasAttribute('data-tk-has-items');
        this.itemsKey = this.getDataAttribute('has-items', 'items');
        this.location = window.location.href;
    }

    async connectedCallback() {
        if (!this.trackerElement) throw new Error('Tracker Element is missing!');
        if (!this.event) throw new Error('Event is not defined');

        if (this.isAsync) this.init();
        else {
            this.pushListener({
                event: 'tk-tracker-ready',
                element: this.trackerElement,
                action: this.init.bind(this),
            });
        }
    }

    async init() {
        this.attributeList = this.extractAttributesFrom(this);
        if (this.hasItems) this.collectItemAttributes();
        await this.getAsyncAttributes();
        this.pushTracker();
    }

    protected extractAttributesFrom(element: HTMLElement): Attribute[] {
        return Object.keys(element.dataset)
            .filter((key) => !key.startsWith('tk'))
            .map((key) => this.createAttribute(key, element.dataset[key]!))
            .filter(Boolean) as Attribute[];
    }

    protected createAttribute(key: string, value: string): Attribute | null {
        const result = isValidJSON(value);
        return {
            key,
            value: result.isValid ? result.json : parseBooleanOrString(value),
        };
    }

    protected collectItemAttributes() {
        const items = Array.from(this.querySelectorAll<HTMLElement>('[data-tk-item]'));
        const itemAttributes = items.map((item) => this.extractAttributesFrom(item)).map(this.mapToSnakeCase);

        this.attributeList.push({
            key: this.itemsKey,
            value: itemAttributes,
        });
    }

    protected mapToSnakeCase(attributes: Attribute[]): Record<string, string | boolean> {
        return attributes.reduce<Record<string, string | boolean>>((acc, attr) => {
            const key = toSnakeCase(attr.key);

            // Wenn der Wert ein Array ist, konvertiere ihn in einen String (z.B. JSON.stringify)
            if (Array.isArray(attr.value)) {
                acc[key] = JSON.stringify(attr.value);
            } else {
                acc[key] = attr.value;
            }

            return acc;
        }, {});
    }

    async getAsyncAttributes() {
        if (!this.asyncAttributesURL) return;
        await fetchRequest({
            requestURL: this.asyncAttributesURL,
            resolveHandler: this.handleAsyncAttributes.bind(this),
        });
    }

    handleAsyncAttributes(response: TKResponse<AsyncAttributeResponse>) {
        Object.entries(response.dataAsJson.attributes).forEach(([key, value]) => {
            this.attributeList.push({
                key: value,
                value: response.dataAsJson[key],
            } as Attribute);
        });
    }

    pushTracker() {
        const item = {
            event: this.event,
            location: this.location,
            ...this.attributeList.reduce<Record<string, string | boolean | Items>>((acc, attr) => {
                const key = toSnakeCase(attr.key);
                acc[key] = attr.value;
                return acc;
            }, {}),
        };

        this.trackerElement?.dispatchEvent(
            new CustomEvent('tk-tracking-push', { detail: { trackerType: this.trackerType, item } }),
        );
    }
}
